/**
 * GeophysicsToolCard — WP-7 (geophysics + side-scan + bathymetry rasters).
 *
 * Implements Card 7 from `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 * §7.7 / §8.7 for the geophysics layer family. Activates inside the
 * right-rail `ToolCardDock` when `SceneSelection.toolCardKind === 'geophysics'`
 * (see `frontend/src/app/sceneSelection.ts`).
 *
 * Scope (see §7.7):
 *   - Color ramp picker (greyscale / amplitude / domain palettes)
 *   - Contrast + gain sliders with numeric (MONO) readouts
 *   - Depth (m) / time (ms) / channel (ch) controls labelled with units
 *   - Profile / cross-section toggle + placeholder (`query_stats` icon
 *     + "WP-9" note per the WP-7 blocker)
 *   - Opacity slider + blend mode chip group
 *   - Georeference / local-grid status via `<CoordinateModeBadge />`
 *   - Sensor metadata card (read-only): survey line id, vessel, date,
 *     sample-rate identifier (MONO)
 *   - Provenance footer with source / extraction info
 *   - Full state matrix: rest, loading, empty, error, permission-denied,
 *     stale (per `DESIGN_POLICY.md` §5)
 *
 * Adapter contract:
 *   The card is engine-agnostic; it consumes a card-level adapter
 *   (`GeophysicsRasterAdapter`) that the host wires to either
 *   `MapLibreAdapter.setRasterOpacity` (added in this WP as a thin
 *   pass-through) or `Giro3DAdapter.setLayerOpacity` for the opacity
 *   axis, and exposes higher-level helpers (`setColorRamp`,
 *   `setContrast`, `setGain`, `setDepth`, `setTime`, `setChannel`,
 *   `setBlendMode`) that the engines fulfil best-effort.
 *
 *   Tests use a vitest mock implementing the same interface to assert
 *   that the card emits the right adapter calls.
 */

import { useId, useMemo, useState, type ReactNode } from 'react';
import {
  Banner,
  Card,
  Chip,
  Icon,
  Slider,
  Stack,
  Text,
  tokens,
  type IconName,
} from '@/design';
// WP-11 / WP-7 → WP-11 dedupe (NB-06). WP-6 widened the canonical
// `<CoordinateModeBadge>` to natively support the 3-state vocabulary
// (`'georeferenced' | 'local' | 'local-grid'`) and a `frameId` prop,
// rendering `Grid: ${frameId}` for `local-grid` mode by default. The
// geophysics + hydrographic surface contracts (older than WP-6's
// canonical) want the bare `Local grid` label plus the survey-line
// frame id rendered as a sibling mono chip — so the Cards override
// the canonical's `label` prop with the domain string and surface
// the frame id via a neutral `<Chip icon="my_location">` sibling.
// The canonical primitive is the single source of truth (no fork);
// the Cards merely choose how to compose the surface.
import {
  CoordinateModeBadge,
  type CoordinateMode as CanonicalCoordinateMode,
} from '@/components/three-d/CoordinateModeBadge';

export type CoordinateMode = CanonicalCoordinateMode;

const COORDINATE_MODE_LABEL: Record<CoordinateMode, string> = {
  georeferenced: 'Georeferenced',
  local: 'Local frame',
  'local-grid': 'Local grid',
};

// ---------------------------------------------------------------------------
// Public type contract
// ---------------------------------------------------------------------------

export type GeophysicsColorRamp =
  | 'greyscale'
  | 'amplitude'
  | 'magma'
  | 'viridis'
  | 'rdbu';

export type GeophysicsBlendMode = 'normal' | 'multiply' | 'screen' | 'overlay';

export type GeophysicsLifecycleState =
  | 'raw'
  | 'levelled'
  | 'gridded'
  | 'inverted'
  | 'interpreted'
  | 'anomaly'
  | 'low-snr'
  | 'masked';

export type GeophysicsCardState =
  | 'ready'
  | 'loading'
  | 'empty'
  | 'error'
  | 'permission-denied'
  | 'stale';

export interface GeophysicsStyleState {
  readonly opacity: number; // 0..1
  readonly contrast: number; // -1..1
  readonly gain: number; // 0..4 (linear gain multiplier)
  readonly colorRamp: GeophysicsColorRamp;
  readonly blendMode: GeophysicsBlendMode;
  readonly depthM: number;
  readonly timeMs: number;
  readonly channel: number;
}

export interface GeophysicsSensorMetadata {
  readonly surveyLineId?: string;
  readonly vessel?: string;
  readonly surveyDateIso?: string;
  /** e.g. "100 kHz", "455 kHz", "2 ms" — rendered in MONO. */
  readonly sampleRateLabel?: string;
  /** Channel range labels (e.g. "ch 1..8" or "port/stbd"). */
  readonly channelLabel?: string;
}

export interface GeophysicsProvenance {
  readonly source?: string;
  readonly producer?: string;
  readonly extractionMethod?: string;
  readonly contentUri?: string;
}

/**
 * Card-level adapter contract. The host wires this from the running
 * engine adapter (MapLibre or Giro3D) via a small bridge; for WP-7 the
 * card never imports engine modules directly.
 *
 * Implementations should be **idempotent** and tolerant of unknown
 * `layerId`s (they may happen during selection churn).
 */
export interface GeophysicsRasterAdapter {
  setOpacity(layerId: string, opacity: number): void;
  setContrast(layerId: string, contrast: number): void;
  setGain(layerId: string, gain: number): void;
  setColorRamp(layerId: string, ramp: GeophysicsColorRamp): void;
  setBlendMode(layerId: string, mode: GeophysicsBlendMode): void;
  setDepth(layerId: string, depthM: number): void;
  setTime(layerId: string, timeMs: number): void;
  setChannel(layerId: string, channel: number): void;
}

export interface GeophysicsToolCardProps {
  /** Renderable layer id from `@geosoft/viewer-sdk`. Required. */
  layerId: string;
  /** Human-readable layer label (rendered in the card header). */
  layerLabel: string;
  /** Current style state — fully controlled (the host owns the source of truth). */
  style: GeophysicsStyleState;
  /** Style mutation callback. Fires once per user interaction. */
  onStyleChange(next: GeophysicsStyleState): void;
  /** Engine-side adapter; tests pass a mock. Optional so the card
   *  remains pure-UI when the host is still wiring up. */
  adapter?: GeophysicsRasterAdapter;
  /** Sensor metadata for the read-only metadata sub-card. */
  sensorMetadata?: GeophysicsSensorMetadata;
  /** Provenance footer fields. */
  provenance?: GeophysicsProvenance;
  /** Coordinate frame status. */
  coordinateMode?: CoordinateMode;
  /** Optional CRS / frame identifier (`EPSG:32638`, `LOCAL_GRID_A`, ...). */
  coordinateFrameId?: string;
  /** Lifecycle state chips (raw / levelled / gridded / ...). */
  lifecycleStates?: readonly GeophysicsLifecycleState[];
  /** Card-level state for the matrix (loading / empty / error / ...). */
  state?: GeophysicsCardState;
  /** Optional error code surfaced in the error banner (MONO). */
  errorCode?: string;
  /** Optional last-update timestamp for the stale banner (MONO). */
  staleSinceIso?: string;
  /** Initial open state for the cross-section profile placeholder. */
  defaultProfileOpen?: boolean;
  /** Test id forwarded to the outer Card for RTL queries. */
  'data-testid'?: string;
}

/**
 * Pure handler factory exported so consumers (and tests) can wire
 * adapter calls without importing the React component. The card itself
 * also uses this factory so behavior stays in lockstep.
 */
export function createGeophysicsHandlers(args: {
  layerId: string;
  style: GeophysicsStyleState;
  onStyleChange(next: GeophysicsStyleState): void;
  adapter?: GeophysicsRasterAdapter;
}): {
  setOpacity(value: number): void;
  setContrast(value: number): void;
  setGain(value: number): void;
  setColorRamp(ramp: GeophysicsColorRamp): void;
  setBlendMode(mode: GeophysicsBlendMode): void;
  setDepth(value: number): void;
  setTime(value: number): void;
  setChannel(value: number): void;
} {
  const { layerId, style, onStyleChange, adapter } = args;
  const patch = (partial: Partial<GeophysicsStyleState>) =>
    onStyleChange({ ...style, ...partial });
  return {
    setOpacity(value) {
      patch({ opacity: value });
      adapter?.setOpacity(layerId, value);
    },
    setContrast(value) {
      patch({ contrast: value });
      adapter?.setContrast(layerId, value);
    },
    setGain(value) {
      patch({ gain: value });
      adapter?.setGain(layerId, value);
    },
    setColorRamp(ramp) {
      patch({ colorRamp: ramp });
      adapter?.setColorRamp(layerId, ramp);
    },
    setBlendMode(mode) {
      patch({ blendMode: mode });
      adapter?.setBlendMode(layerId, mode);
    },
    setDepth(value) {
      patch({ depthM: value });
      adapter?.setDepth(layerId, value);
    },
    setTime(value) {
      patch({ timeMs: value });
      adapter?.setTime(layerId, value);
    },
    setChannel(value) {
      patch({ channel: value });
      adapter?.setChannel(layerId, value);
    },
  };
}

// ---------------------------------------------------------------------------
// Static label / icon tables
// ---------------------------------------------------------------------------

const COLOR_RAMP_OPTIONS: ReadonlyArray<{
  id: GeophysicsColorRamp;
  label: string;
}> = [
  { id: 'greyscale', label: 'Greyscale' },
  { id: 'amplitude', label: 'Amplitude' },
  { id: 'magma', label: 'Magma' },
  { id: 'viridis', label: 'Viridis' },
  { id: 'rdbu', label: 'RdBu' },
];

const BLEND_MODE_OPTIONS: ReadonlyArray<{
  id: GeophysicsBlendMode;
  label: string;
  icon: IconName;
}> = [
  { id: 'normal', label: 'Normal', icon: 'gradient' },
  { id: 'multiply', label: 'Multiply', icon: 'gradient' },
  { id: 'screen', label: 'Screen', icon: 'gradient' },
  { id: 'overlay', label: 'Overlay', icon: 'gradient' },
];

const LIFECYCLE_ICON: Record<
  GeophysicsLifecycleState,
  { icon: IconName; label: string; variant: 'neutral' | 'success' | 'warning' | 'info' | 'tertiary' }
> = {
  raw: { icon: 'graphic_eq', label: 'Raw', variant: 'neutral' },
  levelled: { icon: 'tune', label: 'Levelled', variant: 'info' },
  gridded: { icon: 'grid_4x4', label: 'Gridded', variant: 'info' },
  inverted: { icon: 'tune', label: 'Inverted', variant: 'tertiary' },
  interpreted: { icon: 'edit_note', label: 'Interpreted', variant: 'success' },
  anomaly: { icon: 'warning', label: 'Anomaly', variant: 'warning' },
  'low-snr': { icon: 'warning', label: 'Low SNR', variant: 'warning' },
  masked: { icon: 'visibility_off', label: 'Masked', variant: 'neutral' },
};

// Bounded numeric ranges. Keep modest so the slider thumb stays usable.
const RANGE = {
  opacity: { min: 0, max: 1, step: 0.05 },
  contrast: { min: -1, max: 1, step: 0.05 },
  gain: { min: 0, max: 4, step: 0.1 },
  depthM: { min: 0, max: 5000, step: 1 },
  timeMs: { min: 0, max: 60000, step: 10 },
  channel: { min: 1, max: 64, step: 1 },
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GeophysicsToolCard({
  layerId,
  layerLabel,
  style,
  onStyleChange,
  adapter,
  sensorMetadata,
  provenance,
  coordinateMode = 'georeferenced',
  coordinateFrameId,
  lifecycleStates,
  state = 'ready',
  errorCode,
  staleSinceIso,
  defaultProfileOpen = false,
  'data-testid': testId,
}: GeophysicsToolCardProps) {
  const titleId = useId();
  const [profileOpen, setProfileOpen] = useState(defaultProfileOpen);

  const handlers = useMemo(
    () =>
      createGeophysicsHandlers({
        layerId,
        style,
        onStyleChange,
        adapter,
      }),
    [layerId, style, onStyleChange, adapter],
  );

  const handleOpacity = handlers.setOpacity;
  const handleContrast = handlers.setContrast;
  const handleGain = handlers.setGain;
  const handleColorRamp = handlers.setColorRamp;
  const handleBlendMode = handlers.setBlendMode;
  const handleDepth = handlers.setDepth;
  const handleTime = handlers.setTime;
  const handleChannel = handlers.setChannel;

  const stateBanner = useMemo(
    () => renderStateBanner(state, errorCode, staleSinceIso),
    [state, errorCode, staleSinceIso],
  );

  const interactive = state === 'ready';

  return (
    <Card
      data-testid={testId ?? 'geophysics-tool-card'}
      header={
        <Stack direction="row" align="center" gap={2}>
          <Icon name="bolt" size={24} decorative />
          <span id={titleId}>{layerLabel}</span>
        </Stack>
      }
      footer={renderProvenanceFooter(provenance)}
    >
      <Stack direction="column" gap={3} aria-labelledby={titleId}>
        <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
          <CoordinateModeBadge
            mode={coordinateMode}
            label={COORDINATE_MODE_LABEL[coordinateMode]}
          />
          {coordinateFrameId ? (
            <Chip
              icon="my_location"
              label={coordinateFrameId}
              variant="neutral"
              aria-label={`Coordinate frame: ${coordinateFrameId}`}
            />
          ) : null}
          {(lifecycleStates ?? []).map((s) => (
            <Chip
              key={s}
              label={LIFECYCLE_ICON[s].label}
              icon={LIFECYCLE_ICON[s].icon}
              variant={LIFECYCLE_ICON[s].variant}
            />
          ))}
        </Stack>

        {stateBanner}

        {interactive ? (
          <>
            {renderColorRampSection(style.colorRamp, handleColorRamp)}
            {renderSlider({
              label: 'Opacity',
              icon: 'tune',
              value: style.opacity,
              range: RANGE.opacity,
              unit: '',
              valueLabel: formatPercent(style.opacity),
              onChange: handleOpacity,
            })}
            {renderSlider({
              label: 'Contrast',
              icon: 'gradient',
              value: style.contrast,
              range: RANGE.contrast,
              unit: '',
              valueLabel: formatSigned(style.contrast),
              onChange: handleContrast,
            })}
            {renderSlider({
              label: 'Gain',
              icon: 'tune',
              value: style.gain,
              range: RANGE.gain,
              unit: '×',
              valueLabel: `${style.gain.toFixed(1)}×`,
              onChange: handleGain,
            })}
            {renderBlendSection(style.blendMode, handleBlendMode)}
            {renderSlider({
              label: 'Depth',
              icon: 'straighten',
              value: style.depthM,
              range: RANGE.depthM,
              unit: 'm',
              valueLabel: `${style.depthM.toFixed(0)} m`,
              onChange: handleDepth,
            })}
            {renderSlider({
              label: 'Time',
              icon: 'swap_vert',
              value: style.timeMs,
              range: RANGE.timeMs,
              unit: 'ms',
              valueLabel: `${style.timeMs.toFixed(0)} ms`,
              onChange: handleTime,
            })}
            {renderSlider({
              label: 'Channel',
              icon: 'graphic_eq',
              value: style.channel,
              range: RANGE.channel,
              unit: 'ch',
              valueLabel: `ch ${style.channel}`,
              onChange: handleChannel,
            })}
            {renderProfileSection(profileOpen, setProfileOpen)}
          </>
        ) : null}

        {renderSensorMetadataCard(sensorMetadata)}
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

function renderStateBanner(
  state: GeophysicsCardState,
  errorCode: string | undefined,
  staleSinceIso: string | undefined,
): ReactNode {
  if (state === 'ready') return null;
  if (state === 'loading') {
    return (
      <Banner
        severity="info"
        title="Loading geophysics layer"
        body="Fetching tiles and sensor metadata."
        narrationKey="geophysics:loading"
        persistent={false}
      />
    );
  }
  if (state === 'empty') {
    return (
      <Banner
        severity="info"
        title="No geophysics layer selected"
        body="Pick a side-scan or geophysics layer in the scene tree to start tuning."
        narrationKey="geophysics:empty"
        persistent={false}
      />
    );
  }
  if (state === 'error') {
    return (
      <Banner
        severity="error"
        title="Failed to load geophysics layer"
        body="Check the source endpoint and retry."
        errorCode={errorCode}
        narrationKey="geophysics:error"
      />
    );
  }
  if (state === 'permission-denied') {
    return (
      <Banner
        severity="warning"
        title="Permission denied"
        body="You do not have access to this geophysics layer in the current tenant."
        narrationKey="geophysics:permission"
      />
    );
  }
  if (state === 'stale') {
    return (
      <Banner
        severity="warning"
        title="Stale tiles"
        body={
          staleSinceIso
            ? (
              <span>
                Last updated{' '}
                <span
                  style={{
                    fontFamily: tokens.font.family.mono,
                    fontSize: tokens.font.role.engMono.size,
                  }}
                >
                  {staleSinceIso}
                </span>
              </span>
            )
            : 'Tile cache is older than the source.'
        }
        narrationKey="geophysics:stale"
      />
    );
  }
  return null;
}

function renderColorRampSection(
  selected: GeophysicsColorRamp,
  onSelect: (ramp: GeophysicsColorRamp) => void,
): ReactNode {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={1}>
        <Icon name="palette" size={20} decorative />
        <Text variant="caption" as="span">Color ramp</Text>
      </Stack>
      <div
        role="radiogroup"
        aria-label="Color ramp"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: tokens.space[1],
        }}
      >
        {COLOR_RAMP_OPTIONS.map((option) => {
          const isSelected = option.id === selected;
          return (
            <Chip
              key={option.id}
              label={option.label}
              icon="palette"
              variant={isSelected ? 'tertiary' : 'neutral'}
              pressable
              pressed={isSelected}
              onClick={() => onSelect(option.id)}
              aria-label={`Color ramp ${option.label}`}
              data-testid={`geophysics-ramp-${option.id}`}
            />
          );
        })}
      </div>
    </Stack>
  );
}

function renderBlendSection(
  selected: GeophysicsBlendMode,
  onSelect: (mode: GeophysicsBlendMode) => void,
): ReactNode {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={1}>
        <Icon name="gradient" size={20} decorative />
        <Text variant="caption" as="span">Blend mode</Text>
      </Stack>
      <div
        role="radiogroup"
        aria-label="Blend mode"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: tokens.space[1],
        }}
      >
        {BLEND_MODE_OPTIONS.map((option) => {
          const isSelected = option.id === selected;
          return (
            <Chip
              key={option.id}
              label={option.label}
              icon={option.icon}
              variant={isSelected ? 'tertiary' : 'neutral'}
              pressable
              pressed={isSelected}
              onClick={() => onSelect(option.id)}
              aria-label={`Blend mode ${option.label}`}
              data-testid={`geophysics-blend-${option.id}`}
            />
          );
        })}
      </div>
    </Stack>
  );
}

function renderProfileSection(
  open: boolean,
  setOpen: (next: boolean) => void,
): ReactNode {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={1}>
        <Icon name="query_stats" size={20} decorative />
        <Text variant="caption" as="span">Cross-section profile</Text>
      </Stack>
      <Chip
        label={open ? 'Profile on' : 'Profile off'}
        icon="query_stats"
        variant={open ? 'tertiary' : 'neutral'}
        pressable
        pressed={open}
        onClick={() => setOpen(!open)}
        aria-label="Toggle cross-section profile"
        data-testid="geophysics-profile-toggle"
      />
      {open ? (
        <div
          role="note"
          aria-label="Cross-section profile placeholder"
          style={{
            border: `${tokens.border.width.thin}px dashed ${tokens.color.surface[3]}`,
            borderRadius: tokens.radius.md,
            padding: tokens.space[3],
            color: tokens.color.ink.secondary,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.body.size,
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
          }}
        >
          <Icon name="query_stats" size={20} decorative />
          <span>
            Cross-section profile coming with WP-9 (charting primitive).
          </span>
        </div>
      ) : null}
    </Stack>
  );
}

interface SliderRowOptions {
  label: string;
  icon: IconName;
  value: number;
  range: { min: number; max: number; step: number };
  unit: string;
  valueLabel: string;
  onChange(value: number): void;
}

function renderSlider({
  label,
  icon,
  value,
  range,
  unit,
  valueLabel,
  onChange,
}: SliderRowOptions): ReactNode {
  const ariaLabel = unit ? `${label} (${unit})` : label;
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" justify="space-between" gap={2}>
        <Stack direction="row" align="center" gap={1}>
          <Icon name={icon} size={20} decorative />
          <Text variant="caption" as="span">
            {label}
            {unit ? ` (${unit})` : ''}
          </Text>
        </Stack>
        <span
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.secondary,
          }}
          data-testid={`geophysics-${label.toLowerCase()}-readout`}
        >
          {valueLabel}
        </span>
      </Stack>
      <div style={{ minHeight: tokens.density.touchMin, display: 'flex', alignItems: 'center' }}>
        <Slider
          aria-label={ariaLabel}
          min={range.min}
          max={range.max}
          step={range.step}
          value={value}
          onChange={onChange}
          data-testid={`geophysics-${label.toLowerCase()}-slider`}
        />
      </div>
    </Stack>
  );
}

function renderSensorMetadataCard(meta: GeophysicsSensorMetadata | undefined): ReactNode {
  if (!meta) return null;
  const rows: Array<{ label: string; value: string; mono?: boolean }> = [];
  if (meta.surveyLineId) rows.push({ label: 'Survey line', value: meta.surveyLineId, mono: true });
  if (meta.vessel) rows.push({ label: 'Vessel', value: meta.vessel });
  if (meta.surveyDateIso) rows.push({ label: 'Survey date', value: meta.surveyDateIso, mono: true });
  if (meta.sampleRateLabel) rows.push({ label: 'Sample rate', value: meta.sampleRateLabel, mono: true });
  if (meta.channelLabel) rows.push({ label: 'Channels', value: meta.channelLabel, mono: true });
  if (rows.length === 0) return null;
  return (
    <section
      aria-label="Sensor metadata"
      data-testid="geophysics-sensor-metadata"
      style={{
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.md,
        padding: tokens.space[2],
        background: tokens.color.surface[1],
      }}
    >
      <Stack direction="row" align="center" gap={1} style={{ marginBottom: tokens.space[1] }}>
        <Icon name="info" size={20} decorative />
        <Text variant="caption" as="span">Sensor metadata (read-only)</Text>
      </Stack>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: tokens.space[1],
          columnGap: tokens.space[2],
          margin: 0,
        }}
      >
        {rows.map((row) => (
          <div key={row.label} style={{ display: 'contents' }}>
            <dt
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.muted,
                margin: 0,
              }}
            >
              {row.label}
            </dt>
            <dd
              style={{
                margin: 0,
                fontFamily: row.mono ? tokens.font.family.mono : tokens.font.family.ui,
                fontSize: row.mono
                  ? tokens.font.role.engMono.size
                  : tokens.font.role.body.size,
                color: tokens.color.ink.primary,
              }}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function renderProvenanceFooter(provenance: GeophysicsProvenance | undefined): ReactNode {
  if (!provenance || (!provenance.source && !provenance.producer && !provenance.extractionMethod && !provenance.contentUri)) {
    return null;
  }
  const parts: ReactNode[] = [];
  if (provenance.source) parts.push(<span key="src">Source: {provenance.source}</span>);
  if (provenance.producer) parts.push(<span key="prod">Producer: {provenance.producer}</span>);
  if (provenance.extractionMethod) parts.push(<span key="ext">Method: {provenance.extractionMethod}</span>);
  if (provenance.contentUri) {
    parts.push(
      <span
        key="uri"
        style={{
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMeta.size,
        }}
      >
        {provenance.contentUri}
      </span>,
    );
  }
  return (
    <Stack direction="row" align="center" gap={2} style={{ flexWrap: 'wrap' }}>
      <Icon name="info" size={16} decorative />
      {parts.map((node, idx) => (
        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center' }}>
          {idx > 0 ? <span style={{ marginRight: tokens.space[1] }}>·</span> : null}
          {node}
        </span>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Number formatting helpers
// ---------------------------------------------------------------------------

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatSigned(value: number): string {
  const fixed = value.toFixed(2);
  return value >= 0 ? `+${fixed}` : fixed;
}
